import React, { useState, useCallback, useContext } from "react";
import PropTypes from "prop-types";

import ListItem from "@mui/material/ListItem";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import Divider from "@mui/material/Divider";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import List from "@mui/material/List";
import ListSubheader from "@mui/material/ListSubheader";
import ListItemText from "@mui/material/ListItemText";
import makeStyles from "@mui/styles/makeStyles";
import { caseTableOptionsStyles } from "assets/jss/default/components/caseManagementPageStyles.js";
import { CaseManagementParamsContext } from "contexts/CaseManagementParamsContext";
import { styled } from "@mui/material/styles";
import { ConfirmationDialog } from "components/ConfirmationDialog";

const useStyles = makeStyles(caseTableOptionsStyles);

export function CaseManagementPreferenceList({
  preferences,
  selectedPreferenceId,
  onTableViewSelected,
  onDeleteTableView
}) {
  const { savedTableView } = useContext(CaseManagementParamsContext);
  const [preferenceToDelete, setPreferenceToDelete] = useState(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);

  const classes = useStyles();

  const onDeleteSelected = (open, pref) => {
    setPreferenceToDelete(pref);
    setConfirmationOpen(open);
  };

  const deleteTableView = useCallback(() => {
    onDeleteTableView(preferenceToDelete);
    setPreferenceToDelete(null);
    setConfirmationOpen(false);
  }, [onDeleteTableView, preferenceToDelete]);

  return (
    <>
      <List
        subheader={
          <ListSubheader
            disableSticky
            className={classes.caseTableDialogSubheader}
          >
            Current Saved Table View
          </ListSubheader>
        }
      >
        {savedTableView ? (
          <ListItem
            button
            onClick={() => onTableViewSelected(savedTableView.id)}
            selected={selectedPreferenceId === savedTableView.id}
          >
            <WordBreakListItem primary={savedTableView.name} />
            <ListItemSecondaryAction>
              <IconButton
                edge="end"
                onClick={() => onDeleteSelected(true, savedTableView)}
                size="large"
              >
                <DeleteIcon />
              </IconButton>
            </ListItemSecondaryAction>
          </ListItem>
        ) : (
          <div className={classes.noTableViewSelectedText}>
            No table view selected
          </div>
        )}
      </List>
      <Divider />
      <List
        subheader={
          <ListSubheader
            disableSticky
            className={classes.caseTableDialogSubheader}
          >
            Other Saved Table Views
          </ListSubheader>
        }
      >
        {preferences.length > 0 ? (
          preferences.map(pref => {
            return (
              <ListItem
                key={pref.id}
                button
                onClick={() => onTableViewSelected(pref.id)}
                selected={selectedPreferenceId === pref.id}
              >
                <WordBreakListItem primary={pref.name} />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    onClick={() => onDeleteSelected(true, pref)}
                    size="large"
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            );
          })
        ) : (
          <div className={classes.noTableViewSelectedText}>
            No saved table views to display.
          </div>
        )}
      </List>
      <ConfirmationDialog
        open={confirmationOpen}
        title="Are you sure you want to delete this table view?"
        onClose={() => setConfirmationOpen(false)}
        onConfirm={deleteTableView}
      />
    </>
  );
}

CaseManagementPreferenceList.propTypes = {
  preferences: PropTypes.array.isRequired,
  selectedPreferenceId: PropTypes.number,
  onTableViewSelected: PropTypes.func.isRequired,
  onDeleteTableView: PropTypes.func.isRequired
};

const WordBreakListItem = styled(ListItemText)`
  word-break: break-word;
`;
